import { useState } from 'react'
import clsx from 'clsx'
import { useDispatch } from 'react-redux'
import { match } from 'ts-pattern'
import { useGetRouletteBySlug, useSpinRouletteById } from '@/apis/rest/generated/hooks'
import { Confetti } from '@/components/common/confetti'
import Modal from '@/components/common/modals/Modal'
import { ENV_CONFIG } from '@/constants/env/config'
import ROUTES from '@/constants/legacy/constRoutes'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import { useCustomRouter } from '@/containers/hooks'
import { useDebounceCallback } from '@/containers/hooks/common/useDebounceCallback.hook'
import { UserContainer } from '@/containers/users/UserContainer'
import { doSetGlobalNoticeModal } from '@/stores/reduxUI'
import { Bridge } from '@/utils/bridge/bridge'
import appBridgeProvider, { appBridgeLogin, isInAppFlag } from '@/utils/utilBridge'
import { isDateAfter } from '@/utils/utilDate'
import styles from './roulette-content.module.css'

// Constants
const ROULETTE_CONFIG = {
  debounceDelay: 300, // 중복 클릭 방지를 위한 디바운스 시간 (ms)
  additionalSpins: 15, // 룰렛이 목표 지점 전에 추가로 돌 바퀴 수
  animationDuration: 9000, // 룰렛 회전 애니메이션 시간 (ms)
  resultDelay: 1000, // 룰렛 멈춘 후 결과 표시까지의 지연 시간 (ms)
  prepareAngle: -30, // 반대 방향으로 돌릴 각도 (도)
  prepareDelay: 300, // 반대 방향 회전 지속 시간 (ms)
} as const

const ROULETTE_IMAGES_2026_FEB = {
  round: `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/events/roulette/roulette-round-2026-feb.png`,
  pin: `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/events/roulette/roulette-pin-2026-feb.png`,
} as const

const ROULETTE_IMAGES_2026_MARCH = {
  round: `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/events/roulette/roulette-round-2026-march.png`,
  pin: `${ENV_CONFIG.s3HostUrl}/${ENV_CONFIG.s3Folder}/events/roulette/roulette-pin-2026-march.png`,
} as const

// displayIndex에 따른 각도 매핑 (0부터 시작)
const PRIZE_ANGLES = {
  0: 355, // displayIndex 0
  1: 75, // displayIndex 1
  2: 145, // displayIndex 2
  3: 215, // displayIndex 3
  4: 285, // displayIndex 4
} as const

interface RouletteState {
  isOpenBlockModal: boolean
  reward?: string
  isSpined: boolean
  isSpinning: boolean
  isLoading: boolean
  isPreparing: boolean // 반대 방향 회전 상태 추가
  rotation: number
  result: number | null
  formUrl?: string // form URL 추가
}

const useRouletteLogic = ({ slug = '' }: { slug: string }) => {
  const dispatch = useDispatch()
  const bridgeLogin = appBridgeLogin()
  const { push, asPath } = useCustomRouter()
  const { me, meIsLoading } = UserContainer.useContainer()
  const fixedSlug = 'T_roulette_event-2507'

  const [rouletteState, setRouletteState] = useState<RouletteState>({
    isOpenBlockModal: false,
    reward: undefined,
    isSpined: false,
    isSpinning: false,
    isLoading: false,
    isPreparing: false,
    rotation: 0,
    result: null,
    formUrl: undefined, // form URL 초기값 추가
  })

  const { data: rouletteData, refetch: refetchRoulette } = useGetRouletteBySlug(fixedSlug, {
    query: {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!slug,
    },
  })
  const { mutate: spinRoulette } = useSpinRouletteById()

  const canSpin = rouletteData?.canUserSpin && rouletteData.isAvailable

  const handleLogin = () => {
    if (isInAppFlag) {
      appBridgeProvider((bridge: Bridge) =>
        bridge.login().then(() => {
          bridgeLogin(window.getToken())
        })
      )
    } else {
      push({
        pathname: ROUTES.ACCOUNTS.LOGIN,
        query: {
          [QueryStringKeyEnum.ReturnUrl]: asPath,
        },
      })
    }
  }

  const showLoginModal = () => {
    dispatch(
      doSetGlobalNoticeModal({
        buttonType: 'ACTION_CANCEL',
        text: '로그인 후 이용하실 수 있습니다.\n로그인 하시겠습니까?',
        okText: '로그인하기',
        cancelText: '돌아가기',
        onOk: handleLogin,
        visible: true,
      })
    )
  }

  const handleSpin = () => {
    if (!me) {
      showLoginModal()
      return
    }

    if (!canSpin) {
      setRouletteState((prev) => ({ ...prev, isOpenBlockModal: true }))
      return
    }

    if (rouletteState.isSpinning || rouletteState.isLoading || rouletteState.isPreparing) return

    setRouletteState((prev) => ({ ...prev, isLoading: true, result: null }))

    // 백엔드에서 결과 받아오기
    spinRoulette(
      { id: rouletteData.id },
      {
        onSuccess: (data) => {
          const { rewardLabel, displayIndex, taxDeclarationUrl } = data

          setRouletteState((prev) => ({ ...prev, isLoading: false }))

          // displayIndex에 해당하는 각도 가져오기
          const targetAngle = PRIZE_ANGLES[displayIndex as keyof typeof PRIZE_ANGLES]

          // 1단계: 반대 방향으로 잠깐 회전
          const currentRotation = rouletteState.rotation
          const prepareRotation = currentRotation + ROULETTE_CONFIG.prepareAngle

          setRouletteState((prev) => ({
            ...prev,
            isPreparing: true,
            rotation: prepareRotation,
          }))

          // 2단계: 잠깐 후 원래 방향으로 큰 회전 시작
          setTimeout(() => {
            const currentFullSpins = Math.floor(currentRotation / 360)
            const newRotation = (currentFullSpins + ROULETTE_CONFIG.additionalSpins) * 360 + (360 - targetAngle)

            setRouletteState((prev) => ({
              ...prev,
              isPreparing: false,
              isSpinning: true,
              rotation: newRotation,
            }))

            // 애니메이션 완료 후 1초 뒤에 결과 표시
            setTimeout(() => {
              setRouletteState((prev) => ({ ...prev, isSpinning: false }))

              setTimeout(() => {
                setRouletteState((prev) => ({
                  ...prev,
                  isSpined: true,
                  reward: rewardLabel,
                  result: displayIndex,
                  formUrl: taxDeclarationUrl, // form URL 저장
                }))
                refetchRoulette()
              }, ROULETTE_CONFIG.resultDelay)
            }, ROULETTE_CONFIG.animationDuration)
          }, ROULETTE_CONFIG.prepareDelay)
        },
        onError: () => {
          setRouletteState((prev) => ({ ...prev, isLoading: false, isSpinning: false, isPreparing: false }))
        },
      }
    )
  }

  const { debouncedFunction } = useDebounceCallback({
    callback: handleSpin,
    delay: ROULETTE_CONFIG.debounceDelay,
  })

  const closeBlockModal = () => {
    setRouletteState((prev) => ({ ...prev, isOpenBlockModal: false }))
  }

  return {
    rouletteState,
    me,
    meIsLoading,
    rouletteData,
    canSpin,
    debouncedFunction,
    closeBlockModal,
  }
}

const RouletteWheel = ({
  rotation,
  isSpinning,
  isPreparing,
  isLoading,
  onSpin,
  disabled,
  backgroundColor,
}: {
  rotation: number
  isSpinning: boolean
  isPreparing: boolean
  isLoading: boolean
  onSpin: () => void
  disabled: boolean
  backgroundColor: string
}) => {
  // 애니메이션 상태에 따른 transition 설정
  const getTransition = () => {
    if (isPreparing) {
      return `transform ${ROULETTE_CONFIG.prepareDelay}ms ease-out`
    }
    if (isSpinning) {
      return 'transform 9s cubic-bezier(0.18, 0.82, 0.22, 1)'
    }
    return undefined
  }

  const getCurrentRouletteImage = () => {
    const KST_THRESHOLD = '2026-03-01T00:00:00+09:00'
    const now = new Date().toISOString()
    return match(now)
      .when(
        (date) => isDateAfter(date, KST_THRESHOLD),
        () => ROULETTE_IMAGES_2026_MARCH
      )
      .otherwise(() => ROULETTE_IMAGES_2026_FEB)
  }

  return (
    <div className={styles.rouletteContainer} style={{ backgroundColor }}>
      <div className={styles.rouletteWrapper}>
        <img
          src={getCurrentRouletteImage().round}
          alt="룰렛"
          className={styles.rouletteRound}
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: getTransition(),
          }}
        />
        <button
          type="button"
          onClick={onSpin}
          className={styles.roulettePin}
          disabled={disabled || isSpinning || isLoading || isPreparing}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: disabled || isSpinning || isLoading || isPreparing ? 'default' : 'pointer',
          }}
          aria-label="룰렛 돌리기"
        >
          <img
            src={getCurrentRouletteImage().pin}
            alt="룰렛 핀 및 시작 버튼"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </button>
      </div>
    </div>
  )
}

const RouletteResult = ({
  reward,
  backgroundColor,
  formUrl,
}: {
  reward?: string
  backgroundColor: string
  formUrl?: string
}) => {
  const handleFormClick = () => {
    if (formUrl) {
      window.open(formUrl, '_blank')
    }
  }

  return (
    <div className={styles.resultContainer} style={{ backgroundColor }}>
      {reward && <Confetti trigger />}
      <div className={styles.resultCard}>
        <div className={styles.resultHeader}>
          <h2 className={clsx(styles.resultTitle, 'font-heading5-bold')}>
            {reward ? '축하합니다!' : '오늘의 룰렛은 이미 참여 완료'}
          </h2>
        </div>
        <div className={styles.resultContent}>
          {reward ? (
            <>
              <p className={clsx(styles.rewardAmount, 'font-heading2-bold', 'blue-500')}>{reward}</p>
              <p className={clsx(styles.rewardSuccess, 'font-heading5-regular', 'gray-800')}>당첨되었습니다.</p>
              <div className={clsx(styles.rewardNotice, 'font-body3-regular', 'gray-600')}>
                <p>자세한 내용은 아래 유의사항을</p>
                <p>확인해주세요</p>
              </div>
              {formUrl && (
                <div>
                  <div className={clsx(styles.formNotice, 'font-body3-bold', 'gray-600')}>
                    <p>
                      적립금은 아래폼을 통해 개인정보제공 동의 및 재세공과금에 대한 소득세 신고에 동의 하신 분에 대해
                      지급 됩니다.
                    </p>
                    <p className={clsx('font-body5-regular', 'gray-500')}>*재세공과금은 핏펫에서 부담합니다.</p>
                  </div>
                  <button type="button" className={clsx(styles.buttonOk, 'font-body1-bold')} onClick={handleFormClick}>
                    작성하기
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className={clsx('font-heading6-semibold', 'gray-700')}>
                내일 또 도전해서 <br /> 더 많은 혜택을 받아보세요 🎁
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const BlockModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => (
  <Modal open={isOpen}>
    <Modal.Content>
      <Modal.Title>
        <p className={clsx('font-body1-bold')}>이미 룰렛을 돌렸어요</p>
      </Modal.Title>
      <Modal.SubTitle>
        <p className={clsx(styles.blockModalSubTitle, 'font-body3-regular', 'gray-700')}>내일 다시 돌려보세요!</p>
      </Modal.SubTitle>
    </Modal.Content>
    <Modal.Footer>
      <button type="button" className={clsx(styles.buttonOk, 'font-body1-bold')} onClick={onClose}>
        확인
      </button>
    </Modal.Footer>
  </Modal>
)

export const RouletteContent = ({ slug }: { slug: string }) => {
  const { ad } = EventContainer.useContainer()
  const backgroundColor = ad.style ? JSON.parse(ad.style).coverBackgroundColor : '#ffffff'

  const { rouletteState, me, canSpin, debouncedFunction, closeBlockModal, rouletteData, meIsLoading } =
    useRouletteLogic({ slug })

  const shouldShowWheel = (canSpin && !rouletteState.isSpined) || !me
  const isDisabled = rouletteState.isSpinning || rouletteState.isPreparing

  if (!rouletteData || meIsLoading) return null

  return (
    <>
      {shouldShowWheel ? (
        <RouletteWheel
          rotation={rouletteState.rotation}
          isSpinning={rouletteState.isSpinning}
          isPreparing={rouletteState.isPreparing}
          isLoading={rouletteState.isLoading}
          onSpin={debouncedFunction}
          disabled={isDisabled}
          backgroundColor={backgroundColor}
        />
      ) : (
        <RouletteResult
          reward={rouletteState.reward}
          backgroundColor={backgroundColor}
          formUrl={rouletteState.formUrl}
        />
      )}

      <BlockModal isOpen={rouletteState.isOpenBlockModal} onClose={closeBlockModal} />
    </>
  )
}
